import 'vue/jsx';
import type { Ref, VNodeChild } from 'vue';
import { computed, nextTick, ref, toValue, watch } from 'vue';
import * as _ from 'lodash-es';
import type { RuleType } from 'async-validator';
import { useNaiveForm } from '@/hooks/common/form';
import { useAuth } from '@/hooks/business/auth';
import { formatValueByDataType, omitObject } from '@/utils/common';
import type { BasicForm, BasicFormProps } from '@/typings/basic-form';
import type { CommonType } from '@/typings/common';
import { useBoolean } from '~/packages/hooks/src';

type Params<T extends BasicForm.BaseData> = {
  show: Ref<boolean>;
  props: BasicFormProps<T>;
};

type FieldSchema<T extends BasicForm.BaseData> = BasicForm.FieldSchema<T> & {
  cacheKey: string;
  rulePath: Ref<string> | string;
  /** 是否显示 */
  shallowIfShow: Ref<boolean> | boolean;
  /** 是否禁用 */
  shallowDisabled: Ref<boolean>;
  /** 是否需要提交 */
  shallowNeedSubmit: Ref<boolean>;
  /** 字段值代理对象 */
  shallowValue: Ref<any> | any | null;
  /** 列宽 */
  shallowColSpan: number | string | undefined;
  /** 验证规则 */
  shallowRules: Ref<App.Global.FormRule[]>;
  /** 自定义渲染 */
  shallowRender?: VNodeChild | JSX.Element | null;
  // /** 组件前缀渲染 */
  // shallowComponentPrefix?: Ref<VNode | JSX.Element | null> | VNode | JSX.Element | null;
  // /** 组件后缀渲染 */
  // shallowComponentSuffix?: Ref<VNode | JSX.Element | null> | VNode | JSX.Element | null;
};

type FiledGroupSchema<T extends BasicForm.BaseData> = BasicForm.FiledGroupSchema<T> & {
  /** 组内字段 */
  fields: FieldSchema<T>[];
  /** 是否显示 */
  shallowIfShow: boolean;
};

/** 表单验证类型 */
const AllRuleTypes: string[] = [
  'string',
  'number',
  'boolean',
  'method',
  'regexp',
  'integer',
  'float',
  'array',
  'object',
  'enum',
  'date',
  'url',
  'hex',
  'email',
  'pattern',
  'any'
];

export function useBasicForm<T extends BasicForm.BaseData>({ show, props }: Params<T>) {
  const { bool: loading, setTrue: startLoading, setFalse: endLoading } = useBoolean();
  const { formRef, validate, restoreValidation } = useNaiveForm();

  const { hasAuth } = useAuth();
  /** 执行操作类型 */
  const action: Ref<BasicForm.ActionType> = ref(null);

  /** 表单数据 */
  const model: Ref<T> = ref({}) as Ref<T>;

  /** 原始表单数据 */
  const originalModel: Ref<T> = ref({}) as Ref<T>;

  /** 原始字段 */
  const originalFields: Ref<BasicForm.FieldSchema<T>[]> = ref([]);
  if (Array.isArray(props.fields)) {
    originalFields.value = props.fields;
  } else {
    originalFields.value = props.fields({
      data: model,
      operation: computed(() => props.operation)
    });
  }

  /** 表单字段 */
  const formFields: Ref<FieldSchema<T>[]> = ref([]);

  /** 字段分组，字段没有设置groupKey的将放在一个默认分组里，并显示在最上方 */
  const groups: Ref<FiledGroupSchema<T>[] | null> = computed(() => {
    if (props.fieldGroups && props.fieldGroups.length > 0) {
      const groupsArr = props.fieldGroups.map(group => {
        let shallowIfShow = true;
        if (group.ifShow !== undefined) {
          if (typeof group.ifShow === 'boolean') {
            shallowIfShow = group.ifShow;
          } else {
            shallowIfShow = group.ifShow({
              value: null,
              data: model.value,
              originalData: originalModel.value,
              operation: props.operation
            });
          }
        }
        return {
          ...group,
          shallowIfShow,
          fields: formFields.value.filter(field => field.groupKey === group.key)
        };
      });
      const defaultGroupFields = formFields.value.filter(field => !field.groupKey);
      if (defaultGroupFields && defaultGroupFields.length > 0) {
        groupsArr.splice(0, 0, {
          key: 'default',
          title: '',
          shallowIfShow: true,
          fields: defaultGroupFields
        });
      }
      return groupsArr;
    }
    return null;
  });

  /** 表单校验用数据 */
  // const shallowModel: Ref<T> = computed(() => {
  //   const shallowModelVal = {} as T;
  //   formFields.value.forEach(field => {
  //     if (_.get(model.value, field.key) !== undefined) {
  //       _.set(shallowModelVal, field.key, field.shallowValue);
  //     }
  //     // if (field.key in model.value) {
  //     //   _.set(shallowModelVal, field.key, field.shallowValue);
  //     // }
  //   });
  //   return shallowModelVal;
  // });
  /** 检索字段 */
  const columns = computed<string[]>(() => {
    const fieldColumns: Set<string> = new Set(['id']);
    formFields.value.forEach(field => {
      if (field.shallowIfShow) {
        if ('requestRequiredColumns' in field) {
          field.requestRequiredColumns?.forEach(column => {
            fieldColumns.add(column);
          });
        }
        if (!('type' in field) && !('tempField' in field && field.tempField)) {
          // eslint-disable-next-line no-nested-ternary
          fieldColumns.add(
            // eslint-disable-next-line no-nested-ternary
            field.alias !== undefined
              ? typeof field.alias === 'string'
                ? field.alias
                : field.alias({
                    value: _.get(model.value, field.key, null),
                    data: model.value,
                    operation: toValue(field.shallowDisabled) ? 'view' : props.operation
                  } as BasicForm.FieldCallbackParam<T>)
              : field.key
          );
          if (field.objectIdKey) {
            fieldColumns.add(field.objectIdKey);
          }
        }
      }
    });
    if (props.requestRequiredColumns) {
      props.requestRequiredColumns.forEach(column => {
        fieldColumns.add(column);
      });
    }
    if (props.fieldGroups) {
      props.fieldGroups.forEach(group => {
        group.requestRequiredColumns?.forEach(column => {
          fieldColumns.add(column);
        });
      });
    }
    if (props.requestExcludeColumns && props.requestExcludeColumns.length > 0) {
      props.requestExcludeColumns.forEach(column => {
        fieldColumns.delete(column);
      });
    }
    return Array.from(fieldColumns);
  });
  init();
  watch(
    () => [show, props.operation],
    async () => {
      if (show) {
        formFields.value.forEach(field => {
          if (field.colSpan !== null && field.colSpan !== undefined) {
            if (typeof field.colSpan === 'function') {
              field.shallowColSpan = field.colSpan({
                operation: toValue(field.shallowDisabled) ? 'view' : props.operation
              });
            } else {
              field.shallowColSpan = field.colSpan;
            }
          } else {
            field.shallowColSpan = props.formConfig?.colSpan || '24 s:12 m:12';
          }
        });
        originalModel.value = props.editData as T;
        model.value = props.editData ? (props.editData as T) : ({} as T);
        initModel();
      } else {
        nextTick(() => {
          model.value = {} as T;
          restoreValidation();
        });
      }
    },
    {
      immediate: true
    }
  );

  // 初始化字段，将回调都置换为计算属性
  // eslint-disable-next-line complexity
  function init() {
    if (originalFields.value.length === 0) {
      return;
    }
    const formatFields: FieldSchema<T>[] = [];
    let index = 0;
    for (const field of originalFields.value) {
      if (field.auth !== null && field.auth !== undefined) {
        let hasPermission = true;
        if (typeof field.auth === 'boolean') {
          hasPermission = field.auth;
        } else {
          hasPermission = hasAuth(field.auth);
        }
        if (!hasPermission) {
          // eslint-disable-next-line no-continue
          continue;
        }
      }
      const newField: FieldSchema<T> = {
        cacheKey: `${field.key}_${index}`,
        ..._.cloneDeep(field),
        rulePath: field.key,
        shallowIfShow: true,
        shallowDisabled: ref(false),
        shallowNeedSubmit: ref(false),
        shallowColSpan: '24 s:12 m:12',
        shallowRules: ref([]),
        shallowRender: null,
        shallowValue: null
      };
      if (newField.type !== 'slot' && !('component' in newField)) {
        newField.component = {
          type: 'NInput'
        };
      }
      newField.shallowIfShow = computed(() => {
        if (newField.groupKey && groups.value) {
          for (const group of groups.value) {
            if (group.key === newField.groupKey && !group.shallowIfShow) {
              return false;
            }
          }
        }
        if (field.ifShow !== null && field.ifShow !== undefined) {
          if (typeof field.ifShow === 'boolean') {
            return field.ifShow;
          }
          // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
          const value = (field.ifShow! as Function)({
            value: _.get(model.value, field.key, null),
            data: model.value,
            originalData: originalModel.value,
            operation: props.operation,
            disabled: toValue(newField.shallowDisabled)
          } as BasicForm.FieldCallbackParam<T>);
          return value;
        }
        return true;
      });
      if (newField.type !== 'slot') {
        newField.vModelField = 'vModelField' in field && field.vModelField ? field.vModelField : 'value';
        if (field.disabled !== null && field.disabled !== undefined) {
          newField.shallowDisabled = computed(() => {
            if (typeof field.disabled === 'boolean') {
              return field.disabled;
            }
            const value = field.disabled!({
              value: _.get(model.value, field.key, null),
              data: model.value,
              originalData: originalModel.value,
              operation: props.operation
            } as BasicForm.FieldCallbackParam<T>);
            return value;
          });
        }
        if (newField.needSubmit !== null && newField.needSubmit !== undefined) {
          newField.shallowNeedSubmit = computed(() => {
            if (typeof newField.needSubmit === 'boolean') {
              return newField.needSubmit;
            }
            const value = newField.needSubmit!({
              value: _.get(model.value, field.key, null),
              data: model.value,
              originalData: originalModel.value,
              operation: props.operation,
              disabled: toValue(newField.shallowDisabled)
            } as BasicForm.FieldCallbackParam<T>);
            return value;
          });
        }
        if (newField.alias && newField.dataType !== 'array') {
          if (typeof newField.alias === 'string') {
            newField.rulePath = newField.alias;
          } else {
            newField.rulePath = newField.alias({
              value: _.get(model.value, newField.key, null),
              data: model.value,
              operation: toValue(newField.shallowDisabled) ? 'view' : props.operation
            } as BasicForm.FieldCallbackParam<T>);
          }
        }
        if (newField.required || newField.rules) {
          newField.shallowRules = computed(() => {
            const rules: App.Global.FormRule[] = [];
            if (newField.required) {
              let required = false;
              if (typeof newField.required === 'boolean') {
                required = newField.required;
              } else {
                required = newField.required({
                  value: _.get(model.value, field.key, null),
                  data: model.value!,
                  originalData: originalModel.value,
                  operation: props.operation,
                  disabled: toValue(newField.shallowDisabled)
                });
              }
              if (required) {
                rules.push({
                  key: toValue(newField.rulePath),
                  required: true,
                  type:
                    // eslint-disable-next-line no-nested-ternary
                    newField.ruleType !== undefined
                      ? newField.ruleType
                      : AllRuleTypes.includes(newField.dataType)
                        ? (newField.dataType as RuleType)
                        : 'string',
                  message: getPrefixWord(newField.component?.type) + newField.title
                  // trigger: newField.dataType === 'string' ? ['input', 'change'] : 'change',
                });
              }
            }
            if (newField.rules) {
              if (Array.isArray(newField.rules)) {
                rules.push(...newField.rules);
              } else if (newField.rules instanceof Function) {
                rules.push(
                  ...newField
                    .rules({
                      value: _.get(model.value, toValue(newField.rulePath), null),
                      data: model.value!,
                      originalData: originalModel.value,
                      operation: props.operation,
                      disabled: toValue(newField.shallowDisabled)
                    })
                    .map(rule => {
                      return {
                        ...rule,
                        key: toValue(newField.rulePath)
                      };
                    })
                );
              }
            }
            // if (field.key.includes('.')) {
            //   return nestedObjectFromString(field.key.substring(field.key.indexOf('.') + 1), rules);
            // }
            return rules;
          });
        }
        if (newField.component) {
          if (newField.component.props && typeof newField.component.props === 'function') {
            const propsFn = newField.component.props;
            newField.component.props = computed(() => {
              return propsFn({
                value: _.get(model.value, field.key, null),
                data: model.value!,
                originalData: originalModel.value,
                operation: props.operation,
                disabled: toValue(newField.shallowDisabled)
              });
            });
          }
          if (newField.component.slots && typeof newField.component.slots === 'function') {
            const slotsFn = newField.component.slots;
            newField.component.slots = computed(() => {
              return slotsFn({
                value: _.get(model.value, field.key, null),
                data: model.value!,
                originalData: originalModel.value,
                operation: props.operation,
                disabled: toValue(newField.shallowDisabled)
              });
            });
          }
          if (newField.component.registerScope && typeof newField.component.registerScope === 'function') {
            const registerScopeFn = newField.component.registerScope;
            const registerComputedFn = computed(() => {
              return registerScopeFn({
                value: _.get(model.value, field.key, null),
                data: model.value!,
                originalData: originalModel.value,
                operation: props.operation,
                disabled: toValue(newField.shallowDisabled)
              });
            });
            watch(registerComputedFn, val => {
              newField.component!.register = val;
            });
          }
          // if (newField.component.prefix && typeof newField.component.prefix === 'function') {
          //   const prefixFn = newField.component.prefix;
          //   newField.shallowComponentPrefix = computed(() => {
          //     return prefixFn({
          //       value: _.get(model.value, field.key, null),
          //       data: model.value!,
          //       originalData: originalModel.value,
          //       operation: props.operation,
          //       disabled: toValue(newField.shallowDisabled)
          //     });
          //   });
          // }
          // if (newField.component.suffix) {
          //   // eslint-disable-next-line max-depth
          //   if (typeof newField.component.suffix === 'function') {
          //     const suffixFn = newField.component.suffix;
          //     newField.shallowComponentSuffix = computed(() => {
          //       return suffixFn({
          //         value: _.get(model.value, field.key, null),
          //         data: model.value!,
          //         originalData: originalModel.value,
          //         operation: props.operation,
          //         disabled: toValue(newField.shallowDisabled)
          //       });
          //     });
          //   } else {
          //     newField.shallowComponentSuffix = newField.component.suffix;
          //   }
          // }
        }
        if (newField.render) {
          const renderFn = newField.render;
          const shallowRender = computed(() => {
            return renderFn({
              value: _.get(model.value, field.key, null),
              data: model.value,
              originalData: originalModel.value,
              operation: props.operation,
              disabled: toValue(newField.shallowDisabled)
            });
          });
          watch(shallowRender, val => {
            newField.shallowRender = val;
          });
        }
      }
      formatFields.push(newField);
      index += 1;
    }
    formFields.value = formatFields;
  }

  /**
   * 初始化表单数据V2
   *
   * 使用计算属性，get和set方式代理值
   */
  async function initModel() {
    let initing = true;
    if (props.operation === 'add') {
      const emptyModel: any = { id: null, ...(props.editData ?? {}) };
      formFields.value.forEach(field => {
        if (props.editData && _.has(props.editData, field.key)) {
          _.set(emptyModel, field.key, _.get(props.editData, field.key));
        } else if ('defaultValue' in field && field.defaultValue !== undefined) {
          _.set(emptyModel, field.key, field.defaultValue);
        } else if (field.dataType === 'array') {
          _.set(emptyModel, field.key, []);
        } else if (field.dataType === 'object') {
          _.set(emptyModel, field.key, null);
          // } else if (field.key.includes('.')) {
          // _.set(emptyModel, field.key, {});
        } else {
          _.set(emptyModel, field.key, null);
        }
      });
      model.value = emptyModel;
    } else if (!props.isEditOriginalData && props.editData && props.editData.id !== null && props.findOneFn) {
      action.value = 'fetch';
      startLoading();
      try {
        // const { data } = await props.findOneFn(props.editData.id as string, columns.value);
        // originalModel.value = data as T;
        // if (data !== null) {
        //   const tempModel = { ...data, id: props.editData.id } as T;
        //   model.value = tempModel;
        // }
        const tempModel = {} as T;
        const { data } = await props.findOneFn(props.editData.id as string, columns.value);
        originalModel.value = data as T;
        if (data !== null) {
          tempModel.id = props.editData.id;
          formFields.value.forEach(field => {
            if (field.type !== 'slot') {
              const value = _.get(data, field.key);
              if (value !== null && value !== undefined) {
                _.set(tempModel, field.key, value);
              }
              if (field.objectIdKey) {
                const objectIdValue = _.get(data, field.objectIdKey);
                if (objectIdValue !== null && objectIdValue !== undefined) {
                  _.set(tempModel, field.objectIdKey, objectIdValue);
                }
              }
            } else if (field.key in data) {
              const value = _.get(data, field.key);
              _.set(tempModel, field.key, value);
            }
          });
          model.value = tempModel;
        }
      } catch (e) {
        console.warn(e);
      } finally {
        endLoading();
        action.value = null;
      }
    }
    // 编辑模式为空值赋默认值
    if (props.operation === 'edit') {
      formFields.value.forEach(field => {
        if (field.key in model.value) {
          let value: any = _.get(model.value, field.key);
          if (value === null || value === undefined) {
            if ('defaultValue' in field && field.defaultValue !== undefined) {
              value = field.defaultValue;
            } else if (field.dataType === 'array') {
              value = [] as never;
            } else if (field.dataType === 'object') {
              value = null;
            }
            _.set(model.value, field.key, value);
          }
        }
      });
    }
    if (model.value) {
      formFields.value.forEach(field => {
        if (field.type === 'slot') {
          field.shallowValue = _.get(model.value, field.key);
        } else {
          field.shallowValue = computed({
            get: () => {
              if (field.get) {
                return field.get({
                  value: _.get(model.value, field.key),
                  data: model.value,
                  originalData: originalModel.value,
                  operation: props.operation,
                  disabled: toValue(field.shallowDisabled)
                });
              } else if (
                (toValue(field.shallowDisabled) ||
                  props.operation === 'view' ||
                  ['date', 'datetime', 'time'].includes(field.dataType)) &&
                _.get(model.value, field.key) !== null &&
                _.get(model.value, field.key) !== undefined
              ) {
                if (field.enumRecord) {
                  const formatValue = field.enumRecord[_.get(model.value, field.key) as string];
                  if (typeof formatValue !== 'string' && 'label' in formatValue) {
                    return formatValue.label;
                  }
                  return formatValue;
                }
                return formatValueByDataType(_.get(model.value, field.key), field.dataType);
              }
              return _.get(model.value, field.key);
            },
            set: setVal => {
              if (field.set) {
                _.set(
                  model.value,
                  field.key,
                  field.set({
                    value: setVal,
                    data: model.value,
                    operation: props.operation,
                    disabled: toValue(field.shallowDisabled)
                  })
                );
              } else {
                _.set(model.value, field.key, setVal);
              }
            }
          });
          if (props.operation !== 'view' && !toValue(field.shallowDisabled)) {
            const unwatch = watch(
              () => field.shallowValue,
              () => {
                if (!show.value) {
                  unwatch();
                } else if (!initing) {
                  nextTick(() => {
                    formRef.value?.validate(undefined, rule => {
                      return rule.key === toValue(field.rulePath);
                    });
                  });
                }
              }
            );
          }
        }
      });
    }
    initing = false;
  }
  /** 获取提交数据 */
  function getSubmitModel() {
    if (props.isEditOriginalData) {
      return model.value;
    }
    const submitModel = _.cloneDeep(model.value) as Partial<T>;
    formFields.value.forEach(field => {
      // if (!toValue(field.shallowIfShow) || toValue(field.shallowDisabled)) {
      if (
        (!toValue(field.shallowIfShow) ||
          (toValue(field.shallowDisabled) &&
            !field.shallowNeedSubmit &&
            !('objectIdKey' in field && field.objectIdKey))) &&
        !(props.operation === 'add' && props.editData && _.has(props.editData, field.key))
      ) {
        // 查询有没有同名参数，且是可用状态
        let hasSameKey = false;
        if (
          formFields.value.find(
            // f => f.key === field.key && f !== field && toValue(f.shallowIfShow) && !toValue(f.shallowDisabled)
            f => f.key === field.key && f !== field && toValue(f.shallowIfShow)
          )
        ) {
          hasSameKey = true;
        }
        if (!hasSameKey) {
          _.unset(submitModel, field.key);
        }
      }
    });
    if (props.operation === 'edit') {
      submitModel.id = props.editData!.id;
    }
    omitObject(submitModel, value => (_.isObject(value) && !_.isArray(value) ? _.isEmpty(value) : false));
    return submitModel;
  }

  return {
    loading,
    startLoading,
    endLoading,
    action,
    formRef,
    groups,
    formFields,
    validate,
    restoreValidation,
    model,
    // shallowModel,
    originalModel,
    initModel,
    getSubmitModel
  };
}
/**
 * 获取表单校验提示前缀
 *
 * @param component
 * @returns
 */
function getPrefixWord(component: CommonType.ComponentType | undefined) {
  switch (component) {
    case 'NInput':
    case 'NInputNumber':
    case 'NAutoComplete':
      return '请输入';
    case 'NSelect':
    case 'NTreeSelect':
    case 'NDatePicker':
    case 'NCheckbox':
    case 'NSwitch':
    case 'NTag':
    case 'DictSelect':
    case 'RadioGroup':
      return '请选择';
    default:
      return '请输入';
  }
}

/**
 * 将字符串转换成嵌套对象
 *
 * @param str
 * @param value
 * @returns
 */
// function nestedObjectFromString(str: string, value: any) {
//   if (!str.includes('.')) {
//     return { [str]: value };
//   }
//   const keys = str.split('.'); // 将字符串按 '.' 分割成数组
//   const result = {}; // 初始化结果对象
//   let current: any = result; // 用于跟踪当前对象

//   // 遍历分割后的键，构建嵌套对象
//   // eslint-disable-next-line no-plusplus
//   for (let i = 0; i < keys.length - 1; i++) {
//     current[keys[i]] = {}; // 创建一个新对象
//     current = current[keys[i]]; // 移动到下一个层级
//   }

//   // 最后一个键赋值为传入的 value
//   current[keys[keys.length - 1]] = value;

//   return result;
// }
